import React, { useCallback, useEffect, useRef, useState } from 'react';
import Portal from '../Portal';

interface PositionedTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  width?: number;
}

const PositionedTooltip: React.FC<PositionedTooltipProps> = ({
  children,
  content,
  disabled = false,
  placement = 'bottom',
  delay = 250,
  width = 320,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });
  const [arrowPos, setArrowPos] = useState<{ top: number; left: number; side: 'left' | 'right' | 'top' | 'bottom' }>({ top: 0, left: 0, side: 'top' });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  const VIEWPORT_MARGIN = 12;
  const ARROW_SIZE = 12;

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

  const calculateTooltipPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left: number;
    let top: number;
    let arrowSide: 'left' | 'right' | 'top' | 'bottom' = 'top';

    if (placement === 'bottom') {
      // Position below the trigger
      top = rect.bottom + VIEWPORT_MARGIN;
      const centerX = rect.left + rect.width / 2;
      left = clamp(centerX - width / 2, VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN);
      arrowSide = 'top';
    } else if (placement === 'top') {
      // Position above the trigger
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
      top = rect.top - tooltipHeight - VIEWPORT_MARGIN;
      const centerX = rect.left + rect.width / 2;
      left = clamp(centerX - width / 2, VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN);
      arrowSide = 'bottom';
    } else if (placement === 'left') {
      // Position to the left
      left = rect.left - width - VIEWPORT_MARGIN;
      const centerY = rect.top + rect.height / 2;
      top = clamp(centerY, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN);
      arrowSide = 'right';
    } else {
      // Position to the right
      left = rect.right + VIEWPORT_MARGIN;
      const centerY = rect.top + rect.height / 2;
      top = clamp(centerY, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN);
      arrowSide = 'left';
    }

    setTooltipPos({ left, top });

    requestAnimationFrame(() => {
      const el = tooltipRef.current;
      if (!el) return;
      const th = el.offsetHeight || 200;
      const tw = el.offsetWidth || width;
      
      if (placement === 'bottom' || placement === 'top') {
        // For top/bottom, adjust arrow horizontally
        const triggerCenterX = rect.left + rect.width / 2;
        const tooltipLeft = left;
        const arrowLeft = triggerCenterX - tooltipLeft;
        setArrowPos({ 
          top: 0,
          left: clamp(arrowLeft - ARROW_SIZE / 2, 8, tw - ARROW_SIZE - 8),
          side: arrowSide
        });
      } else {
        // For left/right, adjust arrow vertically
        const triggerCenterY = rect.top + rect.height / 2;
        const arrowTop = clamp(triggerCenterY - top - ARROW_SIZE / 2, 8, th - ARROW_SIZE - 8);
        setArrowPos({ 
          top: arrowTop,
          left: 0,
          side: arrowSide
        });
      }
    });
  }, [placement, width]);

  const handleMouseEnter = () => {
    if (disabled) return;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
    }
    showTimerRef.current = setTimeout(() => {
      setShowTooltip(true);
      setTimeout(calculateTooltipPosition, 0);
      showTimerRef.current = null;
    }, delay);
  };

  const handleMouseLeave = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setShowTooltip(false);
      }
      hideTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    if (!showTooltip) return;
    const onResize = () => calculateTooltipPosition();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [showTooltip, calculateTooltipPosition]);

  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const getArrowStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      pointerEvents: 'none',
      position: 'absolute',
    };

    if (arrowPos.side === 'top') {
      return {
        ...baseStyle,
        bottom: '100%',
        left: `${arrowPos.left}px`,
        transform: 'translateX(-50%)',
      };
    } else if (arrowPos.side === 'bottom') {
      return {
        ...baseStyle,
        top: '100%',
        left: `${arrowPos.left}px`,
        transform: 'translateX(-50%)',
      };
    } else if (arrowPos.side === 'left') {
      return {
        ...baseStyle,
        right: '100%',
        top: `${arrowPos.top}px`,
        transform: 'translateY(-50%)',
      };
    } else {
      return {
        ...baseStyle,
        left: '100%',
        top: `${arrowPos.top}px`,
        transform: 'translateY(-50%)',
      };
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative"
      >
        {children}
      </div>

      {showTooltip && (
        <Portal>
          <div
            ref={tooltipRef}
            className="z-[9999] shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg fixed px-3 py-2"
            style={{
              left: tooltipPos.left,
              top: tooltipPos.top,
              width: `${width}px`,
            }}
            onMouseEnter={() => {
              setIsTooltipHovered(true);
              if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              setIsTooltipHovered(false);
              if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
              }
              hideTimerRef.current = setTimeout(() => {
                setShowTooltip(false);
                hideTimerRef.current = null;
              }, 150);
            }}
          >
            <div style={getArrowStyle()}>
              {arrowPos.side === 'top' && (
                <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-900" />
              )}
              {arrowPos.side === 'bottom' && (
                <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white dark:border-t-slate-900" />
              )}
              {arrowPos.side === 'left' && (
                <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-t-transparent border-b-transparent border-r-white dark:border-r-slate-900" />
              )}
              {arrowPos.side === 'right' && (
                <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-t-transparent border-b-transparent border-l-white dark:border-l-slate-900" />
              )}
            </div>
            <div className="text-xs text-gray-900 dark:text-gray-100 leading-relaxed">
              {content}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

export default PositionedTooltip;